import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0.6) 100%
  );
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.xl || '16px'};
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  max-width: ${props => props.maxWidth || '500px'};
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 85vh;
    border-radius: 20px 20px 0 0;
    margin-top: auto;
  }

  @media (max-width: 480px) {
    border-radius: 16px 16px 0 0;
  }

  /* Custom scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: ${props => props.noBorder ? 'none' : `1px solid ${props.theme.colors.border || '#e5e7eb'}`};
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem 1.25rem 0.75rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text.secondary || '#6b7280'};
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${props => props.theme.colors.background || '#f3f4f6'};
    color: ${props => props.theme.colors.text.primary || '#111827'};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    width: 28px;
    height: 28px;
    font-size: 1.25rem;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary || '#111827'};
  line-height: 1.4;
  margin-right: 2rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-right: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const ModalBody = styled.div`
  padding: 0 2rem 2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    padding: 0 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0 1.25rem 1.25rem;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem 2rem;
  border-top: 1px solid ${props => props.theme.colors.border || '#e5e7eb'};
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  background-color: ${props => props.theme.colors.background || '#f9fafb'};
  border-radius: 0 0 ${props => props.theme.borderRadius.xl || '16px'} ${props => props.theme.borderRadius.xl || '16px'};

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem 1.5rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 1.25rem 1.25rem;
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.875rem 1.75rem;
  border-radius: ${props => props.theme.borderRadius.lg || '12px'};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  position: relative;
  overflow: hidden;
  min-width: 100px;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 1rem 1.5rem;
    min-width: unset;
  }
  
  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, ${props.theme.colors.primary || '#3b82f6'}, ${props.theme.colors.primaryHover || '#2563eb'});
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background-color: transparent;
    color: ${props.theme.colors.text.secondary || '#6b7280'};
    border: 2px solid ${props.theme.colors.border || '#e5e7eb'};
    
    &:hover {
      background-color: ${props.theme.colors.background || '#f3f4f6'};
      border-color: ${props.theme.colors.text.secondary || '#6b7280'};
      color: ${props.theme.colors.text.primary || '#111827'};
      transform: translateY(-1px);
    }
  `}
  
  ${props => props.variant === 'danger' && `
    background: linear-gradient(135deg, ${props.theme.colors.danger || '#ef4444'}, #dc2626);
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  `}
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 1.5rem;
  font-size: 2.5rem;
  position: relative;

  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  ${props => props.variant === 'success' && `
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
  `}

  ${props => props.variant === 'error' && `
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
  `}

  ${props => props.variant === 'warning' && `
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: white;
    box-shadow: 0 8px 25px rgba(245, 158, 11, 0.3);
  `}
`;

const MessageText = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.text.secondary || '#6b7280'};
  font-size: 1rem;
  line-height: 1.6;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  footer, 
  maxWidth,
  noBorder = false,
  showCloseButton = true 
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent maxWidth={maxWidth}>
        {title && (
          <ModalHeader noBorder={noBorder}>
            <ModalTitle>{title}</ModalTitle>
            {showCloseButton && (
              <CloseButton onClick={onClose} aria-label="Close">
                ×
              </CloseButton>
            )}
          </ModalHeader>
        )}
        <ModalBody>
          {children}
        </ModalBody>
        {footer && (
          <ModalFooter>
            {footer}
          </ModalFooter>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

// Confirmation Modal Component
export const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = 'Confirm Action', 
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger'
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="450px">
      <div style={{ textAlign: 'center', padding: '1rem 0' }}>
        <IconContainer variant="warning">
          ⚠️
        </IconContainer>
        <MessageText>{message}</MessageText>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          {cancelText}
        </Button>
        <Button variant={variant} onClick={onConfirm}>
          {confirmText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Success Modal Component
export const SuccessModal = ({ 
  isOpen, 
  onClose, 
  title = 'Success', 
  message = 'Operation completed successfully!',
  buttonText = 'OK'
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="450px">
      <div style={{ textAlign: 'center', padding: '1rem 0' }}>
        <IconContainer variant="success">
          ✓
        </IconContainer>
        <MessageText>{message}</MessageText>
      </div>
      <ModalFooter>
        <Button variant="primary" onClick={onClose} style={{ margin: '0 auto' }}>
          {buttonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Error Modal Component
export const ErrorModal = ({ 
  isOpen, 
  onClose, 
  title = 'Error', 
  message = 'An error occurred. Please try again.',
  buttonText = 'OK'
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="450px">
      <div style={{ textAlign: 'center', padding: '1rem 0' }}>
        <IconContainer variant="error">
          ✕
        </IconContainer>
        <MessageText>{message}</MessageText>
      </div>
      <ModalFooter>
        <Button variant="primary" onClick={onClose} style={{ margin: '0 auto' }}>
          {buttonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default Modal; 